import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState } from "react";
import { ChevronDown, ChevronUp, AlertTriangle, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface DoseRow {
  category: string;
  threshold?: string;
  dose: string;
  notes?: string;
}

interface DrugDosing {
  drug: string;
  standardDose: string;
  renalAdjustment: DoseRow[];
  hepaticAdjustment: DoseRow[];
  otherConsiderations?: string[];
  references: string;
}

const DOSING_DATA: DrugDosing[] = [
  {
    drug: "Methotrexate",
    standardDose: "7.5–25mg once weekly (oral/SC). Max 25mg/week in RA.",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "Standard dose", notes: "Monitor LFTs and FBC as usual" },
      { category: "Mild impairment", threshold: "eGFR 45–59", dose: "Use with caution. Consider 50% dose reduction", notes: "Increased toxicity risk; more frequent monitoring" },
      { category: "Moderate impairment", threshold: "eGFR 30–44", dose: "Reduce dose by 50–75%. Use with extreme caution", notes: "High risk of toxicity. Consider alternative DMARD" },
      { category: "Severe impairment", threshold: "eGFR <30", dose: "Contraindicated", notes: "Risk of life-threatening myelosuppression and hepatotoxicity" },
    ],
    hepaticAdjustment: [
      { category: "Child-Pugh A (mild)", dose: "Use with caution. Enhanced LFT monitoring", notes: "Baseline LFTs must be within acceptable range" },
      { category: "Child-Pugh B (moderate)", dose: "Avoid if ALT/AST >2× ULN at baseline. Reduce dose", notes: "Significant hepatotoxicity risk" },
      { category: "Child-Pugh C (severe)", dose: "Contraindicated", notes: "Hepatic clearance severely impaired" },
    ],
    otherConsiderations: [
      "Obesity/high BSA: Do not dose above 25mg/week regardless of BSA",
      "Elderly: Start at lower doses (5–10mg/week); renal function declines with age",
      "Folic acid 5mg once weekly (not on MTX day) must be co-prescribed to reduce toxicity",
    ],
    references: "BSR MTX Guideline 2022; BNF; SPC",
  },
  {
    drug: "Hydroxychloroquine",
    standardDose: "5mg/kg/day of actual body weight (not to exceed 400mg/day). Typically 200–400mg daily.",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "Standard dose (≤5mg/kg/day)" },
      { category: "Mild impairment", threshold: "eGFR 45–59", dose: "Standard dose with enhanced retinal monitoring from year 1" },
      { category: "Moderate impairment", threshold: "eGFR 30–44", dose: "Reduce to 5mg/kg/day or below. Enhanced retinal screening from year 1", notes: "Renal impairment increases retinal toxicity risk" },
      { category: "Severe impairment", threshold: "eGFR <30", dose: "Significant caution. Consider dose reduction or alternative. Annual retinal screening from year 1", notes: "SPC states use with caution; accumulation risk" },
    ],
    hepaticAdjustment: [
      { category: "Child-Pugh A", dose: "Standard dose with monitoring" },
      { category: "Child-Pugh B", dose: "Use with caution; hepatic metabolism relevant" },
      { category: "Child-Pugh C", dose: "Avoid if possible. No dose guidance in SPC; seek specialist input" },
    ],
    otherConsiderations: [
      "Retinal toxicity dose-dependent: never exceed 5mg/kg/day of actual body weight",
      "Elderly: weight-based dosing particularly important to avoid overdosing",
      "Safe in pregnancy — do not stop without specialist review",
    ],
    references: "RCOphth Guidelines 2020; SPC; BNF",
  },
  {
    drug: "Sulfasalazine",
    standardDose: "500mg daily titrating to 1g BD (2g/day). Max 3g/day in some protocols.",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "Standard dose" },
      { category: "Mild–moderate impairment", threshold: "eGFR 30–59", dose: "Use with caution; no specific dose recommendation. Consider lower maintenance dose", notes: "Accumulation of sulphapyridine metabolite" },
      { category: "Severe impairment", threshold: "eGFR <30", dose: "Contraindicated or extreme caution. Avoid if possible", notes: "Significant risk of sulphapyridine accumulation" },
    ],
    hepaticAdjustment: [
      { category: "Child-Pugh A", dose: "Standard dose with LFT monitoring" },
      { category: "Child-Pugh B", dose: "Use with caution; reduced dose may be appropriate" },
      { category: "Child-Pugh C", dose: "Avoid", notes: "Hepatic acetylation of sulfapyridine impaired" },
    ],
    otherConsiderations: [
      "Slow acetylators (pharmacogenomic) may have higher sulphapyridine levels",
      "Interacts with digoxin (reduced absorption)",
    ],
    references: "BNF; SPC",
  },
  {
    drug: "Leflunomide",
    standardDose: "20mg daily (loading dose 100mg daily for 3 days used in some centres — avoid due to toxicity risk).",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "20mg daily standard dose" },
      { category: "Mild impairment", threshold: "eGFR 45–59", dose: "Use with caution; consider 10mg daily", notes: "Teriflunomide is renally cleared" },
      { category: "Moderate impairment", threshold: "eGFR 30–44", dose: "10mg daily. Enhanced monitoring", notes: "Accumulation risk increases" },
      { category: "Severe impairment", threshold: "eGFR <30", dose: "Not recommended. SPC advises against use", notes: "Limited data; significant accumulation risk" },
    ],
    hepaticAdjustment: [
      { category: "Child-Pugh A", dose: "Caution. Baseline LFTs must be acceptable" },
      { category: "Child-Pugh B", dose: "Avoid unless no alternative; closely monitor LFTs" },
      { category: "Child-Pugh C", dose: "Contraindicated", notes: "Hepatic metabolism impaired; cholestyramine washout may be difficult" },
    ],
    otherConsiderations: [
      "Long half-life — cholestyramine washout (8g TDS ×11 days) needed before conception or switching",
      "CYP2C9 substrate: interactions with warfarin, phenytoin",
      "Active metabolite teriflunomide concentration can be measured if toxicity suspected",
    ],
    references: "SPC; BNF; BSR Monitoring Guideline",
  },
  {
    drug: "Azathioprine",
    standardDose: "1–3mg/kg/day (usual range 1.5–2.5mg/kg/day in rheumatology). Start at 50mg and titrate.",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "Standard dose guided by TPMT status" },
      { category: "Mild impairment", threshold: "eGFR 45–59", dose: "Reduce to 75% of standard dose" },
      { category: "Moderate impairment", threshold: "eGFR 30–44", dose: "Reduce to 50% of standard dose. Close FBC monitoring", notes: "Accumulation of metabolites" },
      { category: "Severe impairment", threshold: "eGFR <30", dose: "Reduce to 25–50%. Consider alternative. Use with extreme caution" },
    ],
    hepaticAdjustment: [
      { category: "Child-Pugh A", dose: "Standard dose with enhanced LFT monitoring" },
      { category: "Child-Pugh B", dose: "Reduce dose; hepatotoxicity risk increased" },
      { category: "Child-Pugh C", dose: "Avoid. Significant hepatotoxicity risk" },
    ],
    otherConsiderations: [
      "TPMT-deficient (1 in 300): contraindicated — life-threatening myelosuppression",
      "TPMT intermediate activity: reduce dose by 50%",
      "Allopurinol interaction: MUST reduce azathioprine dose by 75% or use alternative xanthine oxidase inhibitor strategy",
      "6-TGN levels available for therapeutic drug monitoring",
    ],
    references: "BNF; SPC; NICE CG; BSR Guidelines",
  },
  {
    drug: "Baricitinib",
    standardDose: "4mg once daily (2mg in elderly >75 years or at infection/VTE risk). RA. 2mg dose option.",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "4mg daily (or 2mg per clinical indication)" },
      { category: "Mild impairment", threshold: "eGFR 45–59", dose: "4mg daily — no adjustment required" },
      { category: "Moderate impairment", threshold: "eGFR 30–44", dose: "Reduce to 2mg daily", notes: "SPC: dose adjustment required at eGFR <60 in some contexts" },
      { category: "Severe impairment", threshold: "eGFR 15–29", dose: "2mg daily maximum. Use with caution", notes: "Limited data; increased exposure" },
      { category: "ESRD", threshold: "eGFR <15", dose: "Not recommended" },
    ],
    hepaticAdjustment: [
      { category: "Child-Pugh A/B", dose: "No dose adjustment required" },
      { category: "Child-Pugh C", dose: "Not recommended — insufficient data" },
    ],
    otherConsiderations: [
      "MHRA restriction: use only where appropriate risk-benefit assessment completed (age, CV risk, VTE, malignancy)",
      "OAT3 inhibitors (probenecid): increase baricitinib exposure — reduce to 2mg daily",
      "Dose reduce to 2mg in patients ≥75 years or those at high infection/VTE risk",
    ],
    references: "SPC; MHRA 2023 Class-wide Safety Review; NICE TA715",
  },
  {
    drug: "Tofacitinib",
    standardDose: "5mg BD (RA) or 10mg BD (UC — not rheumatology). Extended-release 11mg OD available.",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "5mg BD standard" },
      { category: "Moderate impairment", threshold: "eGFR 30–59", dose: "Reduce to 5mg once daily", notes: "Renal clearance is a minor elimination pathway but metabolite accumulation" },
      { category: "Severe impairment", threshold: "eGFR <30", dose: "Reduce to 5mg once daily. Use with caution" },
    ],
    hepaticAdjustment: [
      { category: "Child-Pugh A", dose: "No dose adjustment" },
      { category: "Child-Pugh B (moderate)", dose: "Reduce to 5mg once daily" },
      { category: "Child-Pugh C", dose: "Not recommended" },
    ],
    otherConsiderations: [
      "Strong CYP3A4 inhibitors (e.g. ketoconazole): reduce to 5mg OD",
      "Moderate CYP3A4 inhibitors + strong CYP2C19 inhibitors (e.g. fluconazole): reduce to 5mg OD",
      "MHRA: highest VTE and MACE signals among JAK inhibitors in ORAL Surveillance",
    ],
    references: "SPC; MHRA Safety Review 2021/2023; BNF",
  },
  {
    drug: "Upadacitinib",
    standardDose: "15mg once daily (RA, PsA, axSpA, UC). 30mg OD for Crohn's disease induction.",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "15mg OD standard" },
      { category: "Mild–moderate", threshold: "eGFR 30–59", dose: "15mg OD — no dose adjustment, but monitor closely" },
      { category: "Severe impairment", threshold: "eGFR <30", dose: "Avoid. Not recommended — limited data", notes: "Renal clearance minor but accumulation concern" },
    ],
    hepaticAdjustment: [
      { category: "Child-Pugh A/B", dose: "No dose adjustment required" },
      { category: "Child-Pugh C", dose: "Not recommended" },
    ],
    otherConsiderations: [
      "JAK1-selective — potentially more favourable safety profile vs non-selective JAK inhibitors",
      "Strong CYP3A4 inducers (e.g. rifampicin): avoid combination",
      "Apply MHRA class-wide restrictions on JAK inhibitor use",
    ],
    references: "SPC; MHRA 2023; NICE TA715",
  },
  {
    drug: "Mycophenolate Mofetil (MMF)",
    standardDose: "500mg–1.5g BD (total 1–3g/day). In SLE/LN: 2–3g/day target.",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "Standard dose — up to 3g/day in LN" },
      { category: "Mild–moderate", threshold: "eGFR 25–59", dose: "Doses >1g BD should be avoided — SPC recommendation for transplant; rheumatology: 1g BD max", notes: "Free MPA (active metabolite) increases with renal impairment" },
      { category: "Severe impairment", threshold: "eGFR <25", dose: "Avoid doses >1g BD. Use with caution. Close monitoring", notes: "Significant MPA accumulation" },
    ],
    hepaticAdjustment: [
      { category: "Any degree", dose: "No dose adjustment required (hepatic glucuronidation is minor pathway)" },
    ],
    otherConsiderations: [
      "Antacids and cholestyramine reduce MMF absorption — separate by 2 hours",
      "Teratogenic — pregnancy prevention mandatory. Stop 6 weeks before conception",
      "MPA levels can be monitored (AUC-guided dosing in specialist centres)",
    ],
    references: "SPC; BSR LN Guideline; BNF",
  },
  {
    drug: "Ciclosporin",
    standardDose: "2.5–5mg/kg/day in two divided doses (RA). Lower in SLE/other conditions.",
    renalAdjustment: [
      { category: "Normal", threshold: "eGFR ≥60", dose: "2.5–5mg/kg/day. Monitor creatinine" },
      { category: "Pre-existing renal impairment", threshold: "Any reduction in eGFR", dose: "Use with extreme caution. Ciclosporin is nephrotoxic and will worsen impairment", notes: "Contraindicated if pre-existing renal impairment in most cases" },
      { category: "All patients", threshold: "Any", dose: "Stop if serum creatinine rises >30% above baseline on 2 measurements", notes: "Dose-dependent nephrotoxicity — a major limiting toxicity" },
    ],
    hepaticAdjustment: [
      { category: "Child-Pugh A/B", dose: "Reduce dose; hepatic metabolism (CYP3A4) may be impaired" },
      { category: "Child-Pugh C", dose: "Avoid — significant dose reduction required; unpredictable levels" },
    ],
    otherConsiderations: [
      "Extensive CYP3A4 and P-gp interactions — review all concomitant medications",
      "Drug interactions: NSAIDs worsen nephrotoxicity; statins increase myopathy risk; macrolides increase ciclosporin levels",
      "Blood pressure monitoring essential — hypertension is a common dose-limiting effect",
      "Grapefruit juice significantly increases ciclosporin levels — avoid",
    ],
    references: "BNF; SPC; BSR",
  },
];

const eGFRBands = [
  { label: "All eGFR", value: "all" },
  { label: "eGFR ≥60 (Normal)", value: "normal" },
  { label: "eGFR 45–59 (Mild)", value: "mild" },
  { label: "eGFR 30–44 (Moderate)", value: "moderate" },
  { label: "eGFR <30 (Severe)", value: "severe" },
];

const eGFRBandKeywords: Record<string, string[]> = {
  normal: ["Normal", "≥60", "≥ 60"],
  mild: ["Mild", "45–59", "45-59", "45 to 59"],
  moderate: ["Moderate", "30–44", "30-44", "30 to 44", "30–59", "45–59"],
  severe: ["Severe", "<30", "ESRD", "15–29", "15-29"],
};

function matchesBand(row: DoseRow, band: string): boolean {
  if (band === "all") return true;
  const keywords = eGFRBandKeywords[band] ?? [];
  const rowText = `${row.category} ${row.threshold ?? ""}`;
  return keywords.some((kw) => rowText.includes(kw));
}

function DrugDosingCard({ drug, selectedBand }: { drug: DrugDosing; selectedBand: string }) {
  const [expanded, setExpanded] = useState(false);
  const filteredRenalRows = selectedBand === "all"
    ? drug.renalAdjustment
    : drug.renalAdjustment.filter((row) => matchesBand(row, selectedBand));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        className="w-full text-left p-6 flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex-1">
          <h3 className="text-lg font-bold text-slate-900 mb-1">{drug.drug}</h3>
          <p className="text-sm text-slate-600"><span className="font-medium">Standard dose:</span> {drug.standardDose}</p>
          <p className="text-xs text-slate-400 mt-1">{drug.references}</p>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0 mt-1" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0 mt-1" />}
      </button>

      {expanded && (
        <div className="px-6 pb-6 space-y-6 border-t border-slate-100 pt-4">
          <div>
            <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2 flex-wrap">
              <span className="text-blue-600">Renal Impairment</span> — Dose Adjustments by eGFR
              {selectedBand !== "all" && (
                <span className="text-xs font-normal text-violet-600 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">
                  Filtered: {eGFRBands.find((b) => b.value === selectedBand)?.label}
                </span>
              )}
            </h4>
            {filteredRenalRows.length === 0 ? (
              <div className="text-sm text-slate-500 bg-slate-50 rounded-xl p-3">
                No specific guidance for this eGFR band — use clinical judgment and consult the current SPC.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 pr-4 font-semibold text-slate-700 w-40">Category</th>
                      <th className="text-left py-2 pr-4 font-semibold text-slate-700 w-32">eGFR (ml/min/1.73m²)</th>
                      <th className="text-left py-2 pr-4 font-semibold text-slate-700">Recommended Dose</th>
                      <th className="text-left py-2 font-semibold text-slate-700">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRenalRows.map((row) => (
                      <tr key={row.category} className={`border-b border-slate-100 last:border-0 ${selectedBand !== "all" ? "bg-violet-50" : ""}`}>
                        <td className="py-2.5 pr-4 font-medium text-slate-800">{row.category}</td>
                        <td className="py-2.5 pr-4 text-slate-600">{row.threshold ?? "—"}</td>
                        <td className="py-2.5 pr-4 text-slate-800">{row.dose}</td>
                        <td className="py-2.5 text-slate-500 text-xs">{row.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-800 mb-3">
              <span className="text-amber-600">Hepatic Impairment</span> — Dose Adjustments by Child-Pugh Class
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 pr-4 font-semibold text-slate-700 w-48">Child-Pugh Class</th>
                    <th className="text-left py-2 pr-4 font-semibold text-slate-700">Recommended Dose</th>
                    <th className="text-left py-2 font-semibold text-slate-700">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {drug.hepaticAdjustment.map((row) => (
                    <tr key={row.category} className="border-b border-slate-100 last:border-0">
                      <td className="py-2.5 pr-4 font-medium text-slate-800">{row.category}</td>
                      <td className="py-2.5 pr-4 text-slate-800">{row.dose}</td>
                      <td className="py-2.5 text-slate-500 text-xs">{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {drug.otherConsiderations && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="text-xs font-bold text-blue-800 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                <Info className="h-3.5 w-3.5" /> Additional Considerations
              </h4>
              <ul className="space-y-1">
                {drug.otherConsiderations.map((note) => (
                  <li key={note} className="text-sm text-blue-900 flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5 shrink-0">•</span>
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PharmacistDoseOptimisation() {
  const [selectedBand, setSelectedBand] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = DOSING_DATA.filter((d) => {
    const matchesSearch = search === "" || d.drug.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (selectedBand === "all") return true;
    return d.renalAdjustment.some((row) => matchesBand(row, selectedBand));
  });

  return (
    <PharmacistLayout title="Dose Optimisation">
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">
          <strong>Clinical pharmacist dose optimisation reference.</strong> Tables based on SPCs, BNF, and BSR monitoring guidelines. Always verify against the current SPC and local renal/hepatic dosing guidelines. Clinical judgment required for complex patients.
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="dose-search" className="text-sm font-medium text-slate-700 block mb-1.5">Search drug</label>
            <input
              id="dose-search"
              type="text"
              placeholder="e.g. Methotrexate, Baricitinib..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div>
            <label htmlFor="dose-egfr" className="text-sm font-medium text-slate-700 block mb-1.5">Filter by eGFR band</label>
            <select
              id="dose-egfr"
              value={selectedBand}
              onChange={(e) => setSelectedBand(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
            >
              {eGFRBands.map((band) => (
                <option key={band.value} value={band.value}>{band.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-sm font-bold text-amber-800 mb-1 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Child-Pugh Classification Reminder
          </p>
          <div className="grid grid-cols-3 gap-3 text-xs text-amber-900 mt-2">
            <div><span className="font-bold">Class A (5–6 points):</span> Mild hepatic impairment — use with caution</div>
            <div><span className="font-bold">Class B (7–9 points):</span> Moderate — dose adjustment often required</div>
            <div><span className="font-bold">Class C (10–15 points):</span> Severe — most DMARDs contraindicated</div>
          </div>
        </div>

        <div className="space-y-4">
          {filtered.map((drug) => (
            <DrugDosingCard key={drug.drug} drug={drug} selectedBand={selectedBand} />
          ))}
        </div>
      </div>
    </PharmacistLayout>
  );
}
